import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Order } from '../../types';
import { ordersApi } from '../../services/api';
import { loadingPlansApi, palletTypesApi } from '../../services/loading-plan-api';
import { PalletPreviewPanel } from '../../components/PalletPreviewPanel';
import { PACKING_ALGORITHMS } from '../../types/loading-plan';
import type { PalletPreviewResponse, LoadingPlanResponse } from '../../types/loading-plan';
import { ArrowLeft, Calendar, User, Package, FileText, Layers, ExternalLink, RefreshCw, Printer } from 'lucide-react';

export const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pallet state
  const [loadingPlan, setLoadingPlan] = useState<LoadingPlanResponse | null>(null);
  const [palletPreview, setPalletPreview] = useState<PalletPreviewResponse | null>(null);
  const [isLoadingPallets, setIsLoadingPallets] = useState(false);
  const [algorithm, setAlgorithm] = useState<string>('ShelfNextFit');
  const [fallbackPalletTypeId, setFallbackPalletTypeId] = useState<string | undefined>(undefined);

  useEffect(() => {
    palletTypesApi.getAll()
      .then(types => {
        const epal = types.find(t => t.code === 'EPAL 1');
        if (epal) setFallbackPalletTypeId(epal.id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await ordersApi.getById(id);
        setOrder(data as any as Order);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('No se pudo cargar la comanda');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  // Cargar plan de carga asociado o calcular preview
  useEffect(() => {
    if (!order || !id) return;
    const loadPalletData = async () => {
      setIsLoadingPallets(true);
      try {
        // Intentar obtener LoadingPlan asociado
        const plan = await loadingPlansApi.getByOrder(id);
        if (plan && plan.id) {
          setLoadingPlan(plan);
          setPalletPreview(null);
        } else {
          // Sin plan: calcular preview stateless
          setLoadingPlan(null);
          await calculatePreview();
        }
      } catch {
        // Fallback a preview stateless
        setLoadingPlan(null);
        await calculatePreview();
      } finally {
        setIsLoadingPallets(false);
      }
    };

    const calculatePreview = async () => {
      const validLines = order.lines.filter(l => l.productId && l.quantity > 0);
      const effectivePalletTypeId = order.palletTypeId ?? fallbackPalletTypeId;
      if (!validLines.length || !effectivePalletTypeId) return;
      try {
        const result = await ordersApi.palletPreview({
          lines: validLines.map(l => ({
            productId: l.productId,
            packagingContainerId: l.packagingContainerId ?? null,
            quantity: Math.ceil(l.quantity),
          })),
          algorithm,
          palletTypeId: effectivePalletTypeId,
        });
        setPalletPreview(result);
      } catch {
        // silently fail
      }
    };

    loadPalletData();
  }, [order, id, fallbackPalletTypeId]);

  const handleRecalculate = async () => {
    if (!order) return;
    const validLines = order.lines.filter(l => l.productId && l.quantity > 0);
    const effectivePalletTypeId = order.palletTypeId ?? fallbackPalletTypeId;
    if (!validLines.length || !effectivePalletTypeId) return;
    setIsLoadingPallets(true);
    try {
      const result = await ordersApi.palletPreview({
        lines: validLines.map(l => ({
          productId: l.productId,
          packagingContainerId: l.packagingContainerId ?? null,
          quantity: Math.ceil(l.quantity),
        })),
        algorithm,
        palletTypeId: effectivePalletTypeId,
      });
      setPalletPreview(result);
      setLoadingPlan(null);
    } catch {
      // silently fail
    } finally {
      setIsLoadingPallets(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Comanda no encontrada'}
        </div>
        <button
          onClick={() => navigate('/comandas')}
          className="mt-4 text-green-600 hover:text-green-700 flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          Volver a comandas
        </button>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Pending: 'bg-yellow-100 text-yellow-800',
      InProgress: 'bg-blue-100 text-blue-800',
      Completed: 'bg-green-100 text-green-800',
      Cancelled: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      Pending: 'Pendiente', InProgress: 'En Progreso', Completed: 'Completada', Cancelled: 'Cancelada',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getLoadingPlanStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Draft: 'bg-gray-100 text-gray-700',
      Calculated: 'bg-blue-100 text-blue-700',
      Approved: 'bg-green-100 text-green-700',
      InProgress: 'bg-amber-100 text-amber-700',
      Completed: 'bg-emerald-100 text-emerald-700',
      Outdated: 'bg-red-100 text-red-700',
    };
    const labels: Record<string, string> = {
      Draft: 'Borrador', Calculated: 'Calculado', Approved: 'Aprobado',
      InProgress: 'En Proceso', Completed: 'Completado', Outdated: 'Desactualizado',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Construir PalletPreviewResponse desde el LoadingPlan (virtual pallets)
  const loadingPlanAsPreview: PalletPreviewResponse | null = loadingPlan && loadingPlan.virtualPallets.length > 0
    ? {
        pallets: loadingPlan.virtualPallets.map(vp => ({
          id: vp.id,
          sequenceNumber: vp.sequenceNumber,
          totalBoxes: vp.totalBoxes,
          totalWeightKg: vp.totalWeightKg,
          estimatedHeightCm: vp.estimatedHeightCm,
          items: vp.items.map(i => ({
            productName: i.productName,
            packagingName: i.packagingName,
            numberOfBoxes: i.numberOfBoxes,
            weightKg: i.weightKg,
          })),
          layout: [], // No tenemos layout inline, se carga bajo demanda con el 3D viewer
        })),
        totalPallets: loadingPlan.virtualPallets.length,
        warnings: [],
        linesWithoutPackaging: [],
        palletLengthCm: 120,
        palletWidthCm: 80,
        palletBaseHeightCm: 14.4,
      }
    : null;

  const displayPreview = palletPreview || loadingPlanAsPreview;

  const handlePrint = () => {
    if (id) {
      window.open(`/comandas/${id}`, '_blank');
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/comandas')}
          className="text-green-600 hover:text-green-700 flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          Volver
        </button>
        <div className="flex items-center gap-3">
          {getStatusBadge(order.status)}
          <button
            onClick={() => navigate(`/comandas/editar/${order.id}`)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            Editar
          </button>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column: info + products */}
        <div className="space-y-6">
          {/* Order Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Comanda #{order.id.slice(0, 8)}
            </h1>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <User className="text-green-600 mt-1 shrink-0" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Cliente</p>
                  <p className="font-medium text-gray-900">{order.customerName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="text-green-600 mt-1 shrink-0" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Fecha de pedido</p>
                  <p className="font-medium text-gray-900">{formatDate(order.orderDate)}</p>
                </div>
              </div>
              {order.routeName && (
                <div className="flex items-start gap-3">
                  <Package className="text-green-600 mt-1 shrink-0" size={18} />
                  <div>
                    <p className="text-xs text-gray-500">Ruta</p>
                    <p className="font-medium text-gray-900">{order.routeName}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <User className="text-green-600 mt-1 shrink-0" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Creado por</p>
                  <p className="font-medium text-gray-900">{order.createdBy.name}</p>
                  <p className="text-xs text-gray-400">{formatDateTime(order.createdAt)}</p>
                </div>
              </div>
            </div>
            {order.observations && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-start gap-2">
                  <FileText className="text-green-600 mt-0.5 shrink-0" size={16} />
                  <div>
                    <p className="text-xs text-gray-500">Observaciones</p>
                    <p className="text-gray-900 text-sm">{order.observations}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Products */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="text-green-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">
                Productos ({order.lines.length})
              </h2>
            </div>
            <div className="space-y-2">
              {order.lines.map((line) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">{line.productName}</p>
                    {line.packagingContainerName && (
                      <p className="text-xs text-gray-500">{line.packagingContainerName}</p>
                    )}
                    {line.observations && (
                      <p className="text-xs text-gray-400 mt-0.5">{line.observations}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 text-sm">
                      {line.quantity} {line.unit}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column: pallets */}
        <div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="text-green-600" size={20} />
                Palets
              </h2>
              {loadingPlan && (
                <div className="flex items-center gap-2">
                  {getLoadingPlanStatusBadge(loadingPlan.status)}
                  <Link
                    to={`/logistica/planes-carga/${loadingPlan.id}`}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-xs"
                  >
                    <ExternalLink size={12} />
                    Plan completo
                  </Link>
                </div>
              )}
            </div>

            {/* Metrics resumen si hay datos */}
            {loadingPlan && (
              <div className="flex items-center gap-3 text-xs text-slate-600 bg-green-50 rounded-lg px-3 py-2 border border-green-100 mb-3">
                <span className="font-semibold text-green-800">
                  {loadingPlan.metrics.totalPallets} palet{loadingPlan.metrics.totalPallets !== 1 ? 's' : ''}
                </span>
                <span>·</span>
                <span>{loadingPlan.metrics.totalWeightKg.toFixed(1)} kg</span>
                {loadingPlan.metrics.capacityUtilizationPercent > 0 && (
                  <>
                    <span>·</span>
                    <span>{loadingPlan.metrics.capacityUtilizationPercent.toFixed(0)}% cap.</span>
                  </>
                )}
              </div>
            )}

            {/* Selector de algoritmo (solo para preview stateless) */}
            {!loadingPlan && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-slate-500 mb-1">Algoritmo de montaje</label>
                <div className="flex gap-2">
                  <select
                    value={algorithm}
                    onChange={(e) => setAlgorithm(e.target.value)}
                    className="flex-1 bg-white border border-green-200 text-slate-900 text-xs rounded-md p-2"
                  >
                    {PACKING_ALGORITHMS.map(a => (
                      <option key={a.key} value={a.key}>{a.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleRecalculate}
                    disabled={isLoadingPallets}
                    className="px-3 py-2 border border-green-200 rounded-md text-green-700 hover:bg-green-50 text-xs flex items-center gap-1"
                  >
                    <RefreshCw className={`h-3 w-3 ${isLoadingPallets ? 'animate-spin' : ''}`} />
                    Recalcular
                  </button>
                </div>
              </div>
            )}

            {/* Preview de palets */}
            {isLoadingPallets && (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-3 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
              </div>
            )}
            {!isLoadingPallets && displayPreview && (
              <PalletPreviewPanel preview={displayPreview} />
            )}
            {!isLoadingPallets && !displayPreview && (
              <p className="text-xs text-slate-400 italic py-4 text-center">
                No hay datos de palets disponibles para este pedido.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
